import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import LoginPage from "@/pages/LoginPage";
import SiteViewPage from "@/pages/SiteViewPage";
import ClientViewPage from "@/pages/ClientViewPage";
// Self-service registration disabled — users are created by admin only
// import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import ExecutiveHomePage from "@/pages/ExecutiveHomePage";
import FinancePage from "@/pages/FinancePage";
import FinanceLegacyPage from "@/pages/FinanceLegacyPage";
import AssetsPage from "@/pages/AssetsPage";
import VehiclesPage from "@/pages/VehiclesPage";
import CapitalPlanningPage from "@/pages/CapitalPlanningPage";
import HRPage from "@/pages/HRPage";
import HRHubPage from "@/pages/HRHubPage";
import DocumentsPage from "@/pages/DocumentsPage";
import PermissionGroupsPage from "@/pages/PermissionGroupsPage";
import ImportHubPage from "@/pages/ImportHubPage";
import ReportBuilderPage from "@/pages/ReportBuilderPage";
import FinanceV2Page from "@/pages/FinanceV2Page";
import SpreadsheetsPage from "@/pages/SpreadsheetsPage";
import SettingsPage from "@/pages/SettingsPage";
import SecurityPage from "@/pages/SecurityPage";
import HelpPage from "@/pages/HelpPage";
import SearchPage from "@/pages/SearchPage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import OrgManagementPage from "@/pages/OrgManagementPage";
import CRMPage from "@/pages/CRMPage";
import CRMMasterPage from "@/pages/CRMMasterPage";
import AuditLogPage from "@/pages/AuditLogPage";
import PerformancePage from "@/pages/PerformancePage";
import FeedbackPage from "@/pages/FeedbackPage";
import HSEQPage from "@/pages/HSEQPage";
import IntegrationsAdminPage from "@/pages/IntegrationsAdminPage";
import DataGovernancePage from "@/pages/DataGovernancePage";
import DataProvenancePage from "@/pages/DataProvenancePage";
import OperationsPage from "@/pages/OperationsPage";
import ClientPortalHomePage from "@/pages/ClientPortalHomePage";
import ContractsPage from "@/pages/ContractsPage";
import SalesPage from "@/pages/SalesPage";
import ReportsPage from "@/pages/ReportsPage";
import SupportPage from "@/pages/SupportPage";
import FieldAssetPage from "@/pages/FieldAssetPage";
import TagResolverPage from "@/pages/TagResolverPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordResetPages";
import { CapitalProvider } from "@/context/CapitalContext";
import { PermissionsProvider } from "@/context/PermissionsContext";
import { AIContextProvider } from "@/context/AIContextProvider";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 text-blue-900 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  // Force authenticated users with a temporary password to /security first
  if (user.must_change_password && location.pathname !== "/security") {
    return <Navigate to="/security" replace />;
  }
  // Enforce 2FA policy for executive/admin roles (mandatory)
  if (user.must_enable_2fa && location.pathname !== "/security") {
    return <Navigate to="/security" replace />;
  }
  // Client role is restricted to client-facing surfaces: their portal,
  // Asset Intelligence and HSEQ/Sustainability (all data client-scoped server-side)
  if ((user.role || "").toLowerCase() === "client"
      && !["/portal", "/assets", "/hseq", "/security", "/site"].some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/portal" replace />;
  }
  // Sustainability advisors only see the Sustainability site
  if ((user.role || "").toLowerCase() === "sustainability_advisor"
      && !["/hseq", "/security"].some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/hseq" replace />;
  }
  // HSEQ auditor — read-only. The sidebar hides everything except Feedback &
  // CSAT, HSEQ & Sustainability and HR & Workforce; land them on HSEQ from the
  // root (other routes stay reachable by direct URL per the menu-only scope).
  if ((user.role || "").toLowerCase() === "hseq_auditor" && location.pathname === "/") {
    return <Navigate to="/hseq" replace />;
  }
  // General Administration role has no Executive module — land on HR & Workforce
  if ((user.role || "").toLowerCase() === "admin" && location.pathname === "/") {
    return <Navigate to="/hr" replace />;
  }
  // Financial Administration has no Executive module — land on Financial
  if ((user.role || "").toLowerCase() === "finance_admin" && location.pathname === "/") {
    return <Navigate to="/finance" replace />;
  }
  // Field workers only have HSEQ + HR — land on Safety Site
  if ((user.role || "").toLowerCase() === "field_worker" && location.pathname === "/") {
    return <Navigate to="/hseq" replace />;
  }
  return children;
}

function Public({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 text-blue-900 animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to={location.state?.from || "/"} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Public><LoginPage /></Public>} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/client-view/:token" element={<ClientPortalPage />} />
      <Route path="/" element={<Protected><ExecutiveHomePage /></Protected>} />
      <Route path="/dashboard/legacy" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/finance" element={<Protected><FinancePage /></Protected>} />
      <Route path="/finance/legacy" element={<Protected><FinanceLegacyPage /></Protected>} />
      <Route path="/assets" element={<Protected><CapitalPlanningPage /></Protected>} />
      <Route path="/vehicles" element={<Protected><VehiclesPage /></Protected>} />
      <Route path="/assets/live" element={<Protected><AssetsPage /></Protected>} />
      <Route path="/hr" element={<Protected><HRHubPage /></Protected>} />
      <Route path="/hr/documents" element={<Protected><HRPage /></Protected>} />
      <Route path="/documents" element={<Protected><DocumentsPage /></Protected>} />
      <Route path="/spreadsheets" element={<Protected><SpreadsheetsPage /></Protected>} />
      <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
      <Route path="/security" element={<Protected><SecurityPage /></Protected>} />
      <Route path="/search" element={<Protected><SearchPage /></Protected>} />
      <Route path="/help" element={<Protected><HelpPage /></Protected>} />
      <Route path="/org" element={<Protected><OrgManagementPage /></Protected>} />
      <Route path="/crm" element={<Protected><CRMPage /></Protected>} />
      <Route path="/crm/master" element={<Protected><CRMMasterPage /></Protected>} />
      <Route path="/audit" element={<Protected><AuditLogPage /></Protected>} />
      <Route path="/performance" element={<Protected><PerformancePage /></Protected>} />
      <Route path="/feedback" element={<Protected><FeedbackPage /></Protected>} />
      <Route path="/hseq" element={<Protected><HSEQPage /></Protected>} />
      <Route path="/site/:siteId" element={<Protected><SiteViewPage /></Protected>} />
      <Route path="/client/:clientId" element={<Protected><ClientViewPage /></Protected>} />
      <Route path="/admin/integrations" element={<Protected><IntegrationsAdminPage /></Protected>} />
      <Route path="/admin/data-governance" element={<Protected><DataGovernancePage /></Protected>} />
      <Route path="/admin/data-provenance" element={<Protected><DataProvenancePage /></Protected>} />

      {/* Ver1 hero module #2 — LIVE */}
      <Route path="/operations" element={<Protected><OperationsPage /></Protected>} />

      {/* Ver1 §11 — Client Portal (authenticated, client-scoped) */}
      <Route path="/portal" element={<Protected><ClientPortalHomePage /></Protected>} />

      {/* Permission Groups admin */}
      <Route path="/admin/permission-groups" element={<Protected><PermissionGroupsPage /></Protected>} />

      {/* Import Hub — universal CSV/XLSX importers */}
      <Route path="/admin/imports" element={<Protected><ImportHubPage /></Protected>} />

      {/* Report Builder — RBAC-gated custom queries with NL shortcut */}
      <Route path="/report-builder" element={<Protected><ReportBuilderPage /></Protected>} />

      {/* Finance V2 — per-expense BU Allocation + waterfall Performance */}
      <Route path="/finance/v2/bu-allocation" element={<Protected><FinanceV2Page /></Protected>} />

      {/* Slice 4 — Section 30 modules — LIVE */}
      <Route path="/sales"      element={<Protected><SalesPage /></Protected>} />
      <Route path="/contracts"  element={<Protected><ContractsPage /></Protected>} />
      <Route path="/reports"    element={<Protected><ReportsPage /></Protected>} />
      <Route path="/support"    element={<Protected><SupportPage /></Protected>} />
      <Route path="/asset/:assetId" element={<Protected><FieldAssetPage /></Protected>} />
      <Route path="/a/:tagId" element={<Protected><TagResolverPage /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PermissionsProvider>
            <CapitalProvider>
              <AIContextProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
              </AIContextProvider>
            </CapitalProvider>
          </PermissionsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
